/**
 * Persona data for the "03 · Who Is This For?" home section.
 *
 * Per the 2026-05-18 feedback round:
 *   - Journalists card removed (4 cards left, was 5).
 *   - Per-card CTA links removed (single CTA pair sits below all 4).
 *   - Description + reflection question moved to a hover/focus overlay
 *     so the resting card stays compact: icon + title + a magnifying
 *     glass affordance hinting that there's more on hover.
 *
 * Each persona owns its own brand stripe / icon-tile color trio so
 * the section's color story (navy → blue → gold → green2) sweeps
 * left-to-right across the four cards.
 */

export interface Persona {
  readonly stripeClass: string;
  readonly iconBgClass: string;
  readonly iconStrokeClass: string;
  readonly title: string;
  readonly desc: string;
  readonly quote: string;
  readonly icon: React.ReactNode;
}

export const PERSONAS: ReadonlyArray<Persona> = [
  {
    stripeClass: 'bg-navy',
    // Prototype line 1046: bg=var(--light-blue) (#EAF4F9 = our `ice` token),
    // border=rgba(20,65,117,.12). Our `light-blue` token is a different
    // hex (#B9D9EC) so we explicitly use `ice` for parity.
    iconBgClass: 'bg-ice border-navy/[0.12]',
    iconStrokeClass: 'stroke-navy',
    title: 'Researchers',
    desc:
      'Explore springboard alignment patterns across metro areas, drill into credential production trends, and download data for deeper analysis.',
    quote: '"Which metro areas have the largest shortages of credentials aligned with management occupations?"',
    icon: (
      <>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </>
    ),
  },
  {
    stripeClass: 'bg-blue',
    iconBgClass: 'bg-blue/10 border-blue/20',
    iconStrokeClass: 'stroke-blue',
    title: 'Policymakers',
    desc:
      'Identify where workforce investments are needed, compare metro areas side-by-side, and see credential shortages across all 22 occupational groups.',
    quote: '"How does my metro area compare to peer regions on springboard alignment?"',
    icon: (
      <>
        <rect x="2" y="3" width="20" height="18" rx="2" />
        <line x1="2" y1="8" x2="22" y2="8" />
        <line x1="9" y1="8" x2="9" y2="21" />
      </>
    ),
  },
  {
    stripeClass: 'bg-gold',
    iconBgClass: 'bg-gold/10 border-gold/30',
    iconStrokeClass: 'stroke-gold-dark',
    title: 'Institutional Leaders',
    desc:
      "See how your institution's credential production compares with local demand for springboard occupations, identify where to expand or launch new credentials, and benchmark against peers.",
    quote: '"What programs should we grow to meet local springboard demand?"',
    icon: (
      <>
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </>
    ),
  },
  {
    // Prototype line 1111: stripe=var(--green2), icon bg=var(--green-bg)
    // border=rgba(29,131,72,.18). Our `green2` token matches #1D8348.
    stripeClass: 'bg-green2',
    iconBgClass: 'bg-green2/[0.10] border-green2/[0.18]',
    iconStrokeClass: 'stroke-green2',
    title: 'Educators & Counselors',
    desc:
      'Help students find credential pathways that lead to springboard occupations in your region. See which programs offered by which providers align with high-value local opportunities.',
    quote: '"Which programs in our community lead to springboard occupations?"',
    icon: (
      <>
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c3 3 9 3 12 0v-5" />
      </>
    ),
  },
];
